<script setup>
import { computed } from "vue";
import {
  createPresentationModel,
  presentationPreviewSummary,
  validatePresentation,
} from "@/utils/agentPresentation";

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  themeOptions: {
    type: Array,
    default: () => [],
  },
  imageAssetOptions: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["update:modelValue"]);

const model = computed(() => createPresentationModel(props.modelValue));
const errors = computed(() => validatePresentation(model.value));
const preview = computed(() => presentationPreviewSummary(model.value));
const hasLayoutOverride = computed(() => Boolean(model.value.layout_override));
const withCurrentOption = (options, value) => {
  if (!value || options.some((option) => option.value === value)) return options;
  return [{ label: `${value} (current)`, value }, ...options];
};
const brandOptions = computed(() => withCurrentOption(props.themeOptions, model.value.brand_reference));
const logoOptions = computed(() => withCurrentOption(props.imageAssetOptions, model.value.logo_asset_ref));
const sceneAssetOptions = computed(() => withCurrentOption(props.imageAssetOptions, model.value.scene.asset_ref));
const selectedBrand = computed(() => props.themeOptions.find(
  (option) => option.value === model.value.brand_reference,
));
const previewPrimary = computed(() => model.value.brand_primary || selectedBrand.value?.primary || "#2f4d84");
const previewSecondary = computed(() => model.value.brand_secondary || selectedBrand.value?.secondary || "#ff7a30");
const selectedBrandPaletteMissing = computed(() => Boolean(
  model.value.brand_reference && !selectedBrand.value?.primary && !selectedBrand.value?.secondary,
));

const presenterOptions = [
  { label: "Ravo mascot", value: "mascot" },
  { label: "Photoreal avatar", value: "photoreal" },
];
const themeOptions = [
  { label: "Follow device", value: "system" },
  { label: "Light", value: "light" },
  { label: "Dark", value: "dark" },
];
const sceneOptions = [
  { label: "Tenant/default scene", value: "none" },
  { label: "Static image", value: "image" },
];
const fitOptions = [
  { label: "Cover", value: "cover" },
  { label: "Contain", value: "contain" },
];
const idleOptions = [
  { label: "Pause motion when idle", value: "pause_when_idle" },
  { label: "Static", value: "static" },
];
const motionOptions = [
  { label: "Standard", value: "standard" },
  { label: "Reduced", value: "reduced" },
];
const layoutOptions = [
  { label: "Automatic", value: "auto" },
  { label: "Inventory bubble", value: "inventory_bubble" },
  { label: "Basic hero", value: "basic_hero" },
];

const update = (path, value) => {
  const next = createPresentationModel(model.value);
  if (path.includes(".")) {
    const [parent, child] = path.split(".");
    next[parent] = { ...(next[parent] || {}), [child]: value };
  } else {
    next[path] = value;
  }
  emit("update:modelValue", next);
};

const toggleLayoutOverride = (event) => {
  update(
    "layout_override",
    event.target.checked
      ? { scale: 1, offset_x: 0, offset_y: 0 }
      : null,
  );
};
</script>

<template>
  <section class="presentation-config" aria-labelledby="presentation-heading">
    <div class="presentation-config__heading">
      <div>
        <h3 id="presentation-heading">Kiosk presentation</h3>
        <p>Configure the presenter, brand and safe on-device scene.</p>
      </div>
      <span class="status-badge status-testing">Policy v1</span>
    </div>

    <div class="presentation-config__grid">
      <FloatLabel class="w-full">
        <PDropdown
          :model-value="model.presenter_mode"
          input-id="presenter_mode"
          :options="presenterOptions"
          option-label="label"
          option-value="value"
          class="w-full"
          @update:model-value="update('presenter_mode', $event)"
        />
        <label for="presenter_mode">Presenter</label>
      </FloatLabel>

      <div v-if="model.presenter_mode === 'photoreal'">
        <DInput
          :value="model.avatar_package_ref || ''"
          label="Avatar Package Reference"
          width="100%"
          maxlength="128"
          @input="update('avatar_package_ref', $event.target.value)"
        />
        <small v-if="errors.avatar_package_ref" class="presentation-error">{{ errors.avatar_package_ref }}</small>
      </div>

      <div v-if="model.presenter_mode === 'photoreal'">
        <DInput
          :value="model.avatar_package_version || ''"
          label="Package Version (optional)"
          width="100%"
          maxlength="64"
          @input="update('avatar_package_version', $event.target.value)"
        />
        <small v-if="errors.avatar_package_version" class="presentation-error">{{ errors.avatar_package_version }}</small>
      </div>

      <FloatLabel class="w-full">
        <PDropdown
          :model-value="model.theme_mode"
          input-id="theme_mode"
          :options="themeOptions"
          option-label="label"
          option-value="value"
          class="w-full"
          @update:model-value="update('theme_mode', $event)"
        />
        <label for="theme_mode">Theme</label>
      </FloatLabel>

      <div>
        <FloatLabel class="w-full">
          <PDropdown
            :model-value="model.brand_reference"
            input-id="brand_reference"
            :options="brandOptions"
            option-label="label"
            option-value="value"
            class="w-full"
            filter
            show-clear
            @update:model-value="update('brand_reference', $event)"
          />
          <label for="brand_reference">Brand Theme (optional)</label>
        </FloatLabel>
        <small v-if="errors.brand_reference" class="presentation-error">{{ errors.brand_reference }}</small>
      </div>

      <div>
        <FloatLabel class="w-full">
          <PDropdown
            :model-value="model.logo_asset_ref"
            input-id="logo_asset_ref"
            :options="logoOptions"
            option-label="label"
            option-value="value"
            class="w-full"
            filter
            show-clear
            @update:model-value="update('logo_asset_ref', $event)"
          />
          <label for="logo_asset_ref">Logo Image (optional)</label>
        </FloatLabel>
        <small v-if="errors.logo_asset_ref" class="presentation-error">{{ errors.logo_asset_ref }}</small>
      </div>

      <div>
        <DInput
          :value="model.brand_primary || ''"
          label="Primary Colour (optional)"
          width="100%"
          maxlength="7"
          @input="update('brand_primary', $event.target.value)"
        />
        <small v-if="errors.brand_primary" class="presentation-error">{{ errors.brand_primary }}</small>
      </div>

      <div>
        <DInput
          :value="model.brand_secondary || ''"
          label="Secondary Colour (optional)"
          width="100%"
          maxlength="7"
          @input="update('brand_secondary', $event.target.value)"
        />
        <small v-if="errors.brand_secondary" class="presentation-error">{{ errors.brand_secondary }}</small>
      </div>

      <FloatLabel class="w-full">
        <PDropdown
          :model-value="model.scene.media_type"
          input-id="scene_media_type"
          :options="sceneOptions"
          option-label="label"
          option-value="value"
          class="w-full"
          @update:model-value="update('scene.media_type', $event)"
        />
        <label for="scene_media_type">Scene</label>
      </FloatLabel>

      <div v-if="model.scene.media_type === 'image'">
        <FloatLabel class="w-full">
          <PDropdown
            :model-value="model.scene.asset_ref"
            input-id="scene_asset_ref"
            :options="sceneAssetOptions"
            option-label="label"
            option-value="value"
            class="w-full"
            filter
            @update:model-value="update('scene.asset_ref', $event)"
          />
          <label for="scene_asset_ref">Scene Image</label>
        </FloatLabel>
        <small v-if="errors['scene.asset_ref']" class="presentation-error">{{ errors['scene.asset_ref'] }}</small>
      </div>

      <FloatLabel v-if="model.scene.media_type === 'image'" class="w-full">
        <PDropdown
          :model-value="model.scene.fit"
          input-id="scene_fit"
          :options="fitOptions"
          option-label="label"
          option-value="value"
          class="w-full"
          @update:model-value="update('scene.fit', $event)"
        />
        <label for="scene_fit">Scene Fit</label>
      </FloatLabel>

      <FloatLabel class="w-full">
        <PDropdown
          :model-value="model.scene.idle_policy"
          input-id="scene_idle_policy"
          :options="idleOptions"
          option-label="label"
          option-value="value"
          class="w-full"
          @update:model-value="update('scene.idle_policy', $event)"
        />
        <label for="scene_idle_policy">Idle Scene</label>
      </FloatLabel>

      <div>
        <DInput
          :value="String(model.scene.scrim_alpha)"
          label="Scene Scrim (0-0.85)"
          width="100%"
          inputmode="decimal"
          @input="update('scene.scrim_alpha', $event.target.value)"
        />
        <small v-if="errors['scene.scrim_alpha']" class="presentation-error">{{ errors['scene.scrim_alpha'] }}</small>
      </div>

      <div>
        <DInput
          :value="String(model.scene.blur_dp)"
          label="Scene Blur dp (0-32)"
          width="100%"
          inputmode="decimal"
          @input="update('scene.blur_dp', $event.target.value)"
        />
        <small v-if="errors['scene.blur_dp']" class="presentation-error">{{ errors['scene.blur_dp'] }}</small>
      </div>

      <FloatLabel class="w-full">
        <PDropdown
          :model-value="model.motion_level"
          input-id="motion_level"
          :options="motionOptions"
          option-label="label"
          option-value="value"
          class="w-full"
          @update:model-value="update('motion_level', $event)"
        />
        <label for="motion_level">Motion</label>
      </FloatLabel>

      <FloatLabel class="w-full">
        <PDropdown
          :model-value="model.layout_profile"
          input-id="layout_profile"
          :options="layoutOptions"
          option-label="label"
          option-value="value"
          class="w-full"
          @update:model-value="update('layout_profile', $event)"
        />
        <label for="layout_profile">Layout Profile</label>
      </FloatLabel>
    </div>

    <label class="presentation-config__advanced">
      <input type="checkbox" :checked="hasLayoutOverride" @change="toggleLayoutOverride" />
      Use bounded advanced layout override
    </label>

    <div v-if="model.layout_override" class="presentation-config__grid presentation-config__override">
      <div>
        <DInput
          :value="String(model.layout_override.scale)"
          label="Presenter Scale (0.65-1.60)"
          width="100%"
          inputmode="decimal"
          @input="update('layout_override.scale', $event.target.value)"
        />
        <small v-if="errors['layout_override.scale']" class="presentation-error">{{ errors['layout_override.scale'] }}</small>
      </div>
      <div>
        <DInput
          :value="String(model.layout_override.offset_x)"
          label="Horizontal Offset (-0.50-0.50)"
          width="100%"
          inputmode="decimal"
          @input="update('layout_override.offset_x', $event.target.value)"
        />
        <small v-if="errors['layout_override.offset_x']" class="presentation-error">{{ errors['layout_override.offset_x'] }}</small>
      </div>
      <div>
        <DInput
          :value="String(model.layout_override.offset_y)"
          label="Vertical Offset (-0.50-0.50)"
          width="100%"
          inputmode="decimal"
          @input="update('layout_override.offset_y', $event.target.value)"
        />
        <small v-if="errors['layout_override.offset_y']" class="presentation-error">{{ errors['layout_override.offset_y'] }}</small>
      </div>
    </div>

    <div
      class="presentation-preview"
      :style="{
        '--preview-primary': previewPrimary,
        '--preview-secondary': previewSecondary,
      }"
    >
      <div class="presentation-preview__swatch" aria-hidden="true"></div>
      <div>
        <strong>Policy preview</strong>
        <p>{{ preview.presenter }} · {{ preview.scene }} · {{ model.layout_profile.replaceAll('_', ' ') }}</p>
        <small>
          Preview shows policy only. If a package, version, asset or layout is unavailable or incompatible,
          the kiosk safely falls back to the Ravo mascot and package defaults without blocking the session.
        </small>
        <small v-if="selectedBrandPaletteMissing">
          The selected theme palette will be resolved by the backend and device; this theme-list response did not include preview colours.
        </small>
      </div>
    </div>
  </section>
</template>
